import * as React from 'react';

import { Viewport, ViewSmall, ViewLarge } from 'bernie-view';
import { UitkCardAloha, UitkCardAlohaLink } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkHeading, UitkText } from 'uitk-react-text';

import { PricingInformation } from '.';
import { EbfOfferType } from 'types/search/listings/offer';
import { useStores } from 'utility/hooks';
import { FlightAction, FlightsBargainFareOffer } from '__generated__/api';
import { DetailsAndFaresDialog } from 'components/shared/details-and-fares';
import { focusElement, trackAnalyticsList } from 'utility';
import { Link as RouterLink } from 'react-router-dom';
import FlightsJourneyAvailableFaresInformation = FlightsBargainFareOffer.FlightsJourneyAvailableFaresInformation;

interface EbfOfferProps {
  offer: EbfOfferType;
  openDetails: (
    offerIndex: number,
    availableFaresInformation: FlightsJourneyAvailableFaresInformation,
    isEbfOffer?: boolean
  ) => void;
  offerIndex: number;
  selectedOfferRef: React.Ref<HTMLButtonElement>;
  selectedOfferIndex: number;
  handleOfferSelect: (selectOfferAction: FlightAction.Fragment) => void;
  openForcedChoiceModal?: boolean;
}

export const EbfOffer = ({
  offer: {
    bargainOfferAccessibilityMessage: accessibilityMessage,
    header,
    description,
    image,
    bargainPricingInformation: { price, tripType },
    selectOfferAction: { relativeURI, analyticsList },
    journeys
  },
  openDetails,
  offerIndex,
  selectedOfferRef,
  selectedOfferIndex,
  handleOfferSelect,
  openForcedChoiceModal
}: EbfOfferProps) => {
  const sharedProps = {
    href: relativeURI && relativeURI.relativePath
  };
  const { routeStore, context, analytics } = useStores();

  if (!context || !context.deviceInformation || !context.deviceInformation.mobile) {
    Object.assign(sharedProps, { target: '_blank', rel: 'noopener noreferrer' });
  }

  if (context.deviceInformation.mobile) {
    Object.assign(sharedProps, { role: 'link' });
  }
  const availableFaresInformation = journeys[0]
    .flightsJourneyAvailableFaresInformation as FlightsJourneyAvailableFaresInformation;
  const detailsAndFaresDialogId = `details-and-fares-dialog-${offerIndex}`;

  const analyticsOnEbfOfferSelect = () => {
    trackAnalyticsList(analyticsList, analytics);
  };

  const openSheet = (event: React.MouseEvent<HTMLButtonElement>) => {
    analyticsOnEbfOfferSelect();
    openDetails(offerIndex, availableFaresInformation, true);
    event.preventDefault();
  };

  const closeDialog = () => {
    focusElement(selectedOfferRef);
  };
  const isOfferSelected = selectedOfferIndex === offerIndex;

  const imageAditionalProps = {
    'aria-hidden': 'true'
  };

  return (
    <UitkCardAloha className="all-b-margin-three" padded selected={isOfferSelected}>
      <UitkLayoutGrid columns={12} space="two" data-test-id="listing-summary">
        <>
          <UitkLayoutGridItem colSpan={{ small: 7, medium: 9 }}>
            <UitkLayoutFlex wrap="wrap">
              <Viewport>
                <ViewLarge>
                  <UitkLayoutFlexItem minWidth={40} grow={0}>
                    <div className="all-t-margin-three all-t-padding-half">
                      <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
                        <UitkImage src={image.url} additionalProps={imageAditionalProps} />
                      </UitkFigure>
                    </div>
                  </UitkLayoutFlexItem>
                </ViewLarge>
              </Viewport>
              <Viewport>
                <ViewSmall>
                  <UitkCardAlohaLink>
                    <RouterLink
                      to={{
                        pathname: routeStore.pathname,
                        search: routeStore.mergeQueryString(routeStore.search, {
                          pwaDialog: detailsAndFaresDialogId
                        })
                      }}
                      role="button"
                      data-test-id="select-link"
                      onClick={analyticsOnEbfOfferSelect}
                    >
                      {accessibilityMessage}
                    </RouterLink>
                  </UitkCardAlohaLink>
                </ViewSmall>
                <ViewLarge>
                  <UitkCardAlohaLink>
                    <button ref={selectedOfferRef} onClick={openSheet} data-test-id="select-link">
                      {accessibilityMessage}
                    </button>
                  </UitkCardAlohaLink>
                </ViewLarge>
              </Viewport>
              <UitkLayoutFlexItem grow={1} minWidth="half_width">
                <div data-test-id="ebf-content" className="m-l-margin-four l-l-margin-four xl-l-margin-three">
                  <UitkHeading level={3} size={300}>
                    {header.completeText}
                  </UitkHeading>
                  <UitkText align="left" size={200} spacing="two">
                    {description.completeText}
                  </UitkText>
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkLayoutGridItem>
          <UitkLayoutGridItem colSpan={{ small: 5, medium: 3 }}>
            <UitkLayoutFlexItem grow={1}>
              <div>
                <PricingInformation price={price} tripType={tripType} />
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutGridItem>
        </>
      </UitkLayoutGrid>
      <Viewport>
        <ViewSmall>
          <UitkCardAlohaLink>
            <DetailsAndFaresDialog
              onDialogClose={closeDialog}
              openForcedChoiceModal={openForcedChoiceModal}
              handleOfferSelect={handleOfferSelect}
              showSummaryAndDetails={true}
              detailsAndFaresDialogId={detailsAndFaresDialogId}
              availableFaresInformation={availableFaresInformation}
              itemIndex={0}
            />
          </UitkCardAlohaLink>
        </ViewSmall>
      </Viewport>
    </UitkCardAloha>
  );
};
